import * as THREE from 'three';
import type { PoseProvider } from './PoseProvider';
import type { GeoWatcher } from '../core/GeoWatcher';
import type { OrientationSource } from '../core/OrientationSource';

const DEG2RAD = Math.PI / 180;
/** 端末の想定目線高[m](計画書§7.1) */
const EYE_HEIGHT_M = 1.5;

/**
 * パスB: GPS + DeviceOrientation による3DoF回転+低頻度位置のポーズ供給。
 * iOS Safari / WebXR非対応Android向けフォールバック。
 */
export class SensorPoseProvider implements PoseProvider {
  readonly kind = 'sensor' as const;
  private yawOffsetRad = 0;
  private declinationRad = 0;
  private deviceQuat = new THREE.Quaternion();
  private yawQuat = new THREE.Quaternion();
  private targetQuat = new THREE.Quaternion();
  private prevTargetQuat = new THREE.Quaternion();
  private hasPrevTarget = false;
  private bigJumpStreak = 0;
  private forwardTmp = new THREE.Vector3();
  private hasQuat = false;
  private targetPos = new THREE.Vector3(0, EYE_HEIGHT_M, 0);
  private hasPos = false;
  private snapped = false;

  constructor(
    private geo: GeoWatcher,
    private orientation: OrientationSource,
    magneticDeclinationDeg: number,
  ) {
    this.declinationRad = magneticDeclinationDeg * DEG2RAD;
  }

  async start(): Promise<void> {
    // 購読してからstartする(固定位置モードではstart()が同期的にサンプルを発火するため)
    this.geo.onUpdate((s) => {
      // ENU → three.js: x=east, y=up, z=-north
      this.targetPos.set(s.east, EYE_HEIGHT_M, -s.north);
      this.hasPos = true;
    });
    this.orientation.start();
    this.geo.start();
    const cur = this.geo.current;
    if (cur) {
      this.targetPos.set(cur.east, EYE_HEIGHT_M, -cur.north);
      this.hasPos = true;
    }
  }

  stop(): void {
    this.geo.stop();
    this.orientation.stop();
  }

  setYawOffsetDeg(deg: number): void {
    this.yawOffsetRad = deg * DEG2RAD;
  }

  get ready(): boolean {
    return this.hasPos && this.orientation.ready;
  }

  update(camera: THREE.PerspectiveCamera, _contentGroup: THREE.Group): void {
    if (this.orientation.getQuaternion(this.deviceQuat)) {
      // 真北補正(磁気偏角) + 手動キャリブレーションをY軸回転として前掛けする。
      // 真方位 h_true = h_mag + decl、ヨー角 θ = -h なので補正は -decl。
      this.yawQuat.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, -this.declinationRad + this.yawOffsetRad);
      this.targetQuat.copy(this.yawQuat).multiply(this.deviceQuat);
      if (!this.hasQuat) {
        camera.quaternion.copy(this.targetQuat);
        this.hasQuat = true;
      } else {
        // 大ジャンプの扱い(真上に向けたときのジンバルロックでalphaが暴れると
        // 視線が飛び、B-29が画面外へ「消えた」ように見える — 序盤の消失の主因):
        // 旧実装は大ジャンプを「遅いslerpで追従」していたため、暴れが数フレーム
        // 続くと結局間違った方向へ流れてしまった。
        // 新実装はセンサー列自体の整合性で本物の回転とスパイクを区別する:
        //  - センサー値がフレーム間で暴れている → ノイズ。ほぼ保持(追従しない)
        //  - センサー値が新しい向きで安定 → ユーザーの素早い回転。
        //    数フレーム連続で確認してから追従(50ms程度の遅延は知覚されない)
        const ang = camera.quaternion.angleTo(this.targetQuat);
        const streamAng = this.hasPrevTarget ? this.prevTargetQuat.angleTo(this.targetQuat) : 0;
        this.prevTargetQuat.copy(this.targetQuat);
        this.hasPrevTarget = true;
        let k: number;
        if (ang <= 0.5) {
          this.bigJumpStreak = 0;
          k = 0.35;
        } else if (streamAng < 0.35) {
          this.bigJumpStreak++;
          k = this.bigJumpStreak >= 3 ? 0.3 : 0.05;
        } else {
          this.bigJumpStreak = 0;
          k = 0.02;
        }
        // 天頂±15°を向いている間は方位角が縮退しalphaが本質的に不安定なため、
        // さらに強く減衰させる(ガイダンスが「真上に向けて」と促すためこの姿勢は頻出)
        this.forwardTmp.set(0, 0, -1).applyQuaternion(this.targetQuat);
        if (this.forwardTmp.y > 0.966) k = Math.min(k, 0.08);
        camera.quaternion.slerp(this.targetQuat, k);
      }
    }
    // 初回の位置は即座に確定させる(遠い初期位置から滑ってくる見え方を防ぐ)
    if (!this.snapped) {
      camera.position.copy(this.targetPos);
      if (this.hasPos) this.snapped = true;
      return;
    }
    // 以後はGPS位置へ滑らかに追従。目標との差が小さいうちは動かさない(デッドバンド):
    // GPSノイズを毎フレーム追いかけるとカメラが常に泳ぎ、
    // 固定座標アンカー時はモデルの位置ズレ・ジャンプとして見える
    if (camera.position.distanceTo(this.targetPos) > 2.5) {
      camera.position.lerp(this.targetPos, 0.04);
    }
  }
}
